<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { Mail } from 'lucide-vue-next';
import AppFooter from '@/components/AppFooter.vue';
import { t } from '~/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/contact'));

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

// SEO meta —— useHead 接受函数式 content 实现 i18n 响应式
// （详见 about.vue 同款注释；SSR 永远 'en'，hydration 后切换）。
useHead(() => ({
  title: t('landingPages.contact.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: t('landingPages.contact.seo.description') },
    { name: 'keywords', content: t('landingPages.contact.seo.keywords') },
    { property: 'og:title', content: t('landingPages.contact.seo.title') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:description', content: t('landingPages.contact.seo.description') },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: t('landingPages.contact.seo.title') },
    { name: 'twitter:description', content: t('landingPages.contact.seo.description') },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }, ...buildPathHreflangLinks('/contact')],
}));

/**
 * Contact 选项卡（目前只有 Email Support 一项；保留数组结构方便后续添加 Discord、销售等卡片）。
 * email / discordLink 等结构数据保留硬编码，不进 i18n（地址不随语言变化）。
 */
const contactOptions = computed(() => [
  {
    key: 'emailSupport',
    title: t('landingPages.contact.options.emailSupport.title'),
    description: t('landingPages.contact.options.emailSupport.description'),
    action: t('landingPages.contact.options.emailSupport.action'),
    icon: Mail,
    email: 'hi@veeso.ai',
  },
]);

/**
 * FAQ 列表数据。
 * - 简单 Q/A：answer 字段
 * - 含内联链接的 Q/A：answerBefore + discordText（链接锚文本）+ answerAfter 三段
 *   （discordLink URL 不进 i18n）
 */
const DISCORD_LINK = 'https://discord.gg/twbZEHkuWx';
const faqItems = computed(() => [
  {
    question: t('landingPages.contact.faq.items.responseTime.question'),
    answer: t('landingPages.contact.faq.items.responseTime.answer'),
  },
  {
    question: t('landingPages.contact.faq.items.supportTypes.question'),
    answerBefore: t('landingPages.contact.faq.items.supportTypes.answerBefore'),
    discordLink: DISCORD_LINK,
    discordText: t('landingPages.contact.faq.items.supportTypes.discordText'),
    answerAfter: t('landingPages.contact.faq.items.supportTypes.answerAfter'),
  },
  {
    question: t('landingPages.contact.faq.items.reportBug.question'),
    answerBefore: t('landingPages.contact.faq.items.reportBug.answerBefore'),
    discordLink: DISCORD_LINK,
    discordText: t('landingPages.contact.faq.items.reportBug.discordText'),
    answerAfter: t('landingPages.contact.faq.items.reportBug.answerAfter'),
  },
  {
    question: t('landingPages.contact.faq.items.hiring.question'),
    answer: t('landingPages.contact.faq.items.hiring.answer'),
  },
]);

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <AppNav active-path="/contact" />

    <!-- Hero Section -->
    <section class="relative pt-48 pb-32 overflow-hidden contact-hero">
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-5xl md:text-7xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ $t('landingPages.contact.hero.title') }}
        </h1>
        <p class="text-white/90 text-xl max-w-2xl mx-auto font-medium">
          {{ $t('landingPages.contact.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- Contact Options Section -->
    <section class="py-24 bg-white min-h-[40vh]">
      <div class="container mx-auto px-6 md:px-4">
        <div class="max-w-2xl mx-auto">
          <a
            v-for="option in contactOptions"
            :key="option.title"
            :href="`mailto:${option.email}`"
            class="p-8 rounded-3xl transition-all duration-300 cursor-pointer group flex flex-col items-center text-center"
          >
            <div class="mb-6 p-4 rounded-2xl">
              <component :is="option.icon" class="w-10 h-10 text-black/80" />
            </div>
            <h2
              class="text-3xl font-bold text-black/90 mb-4 group-hover:text-black uppercase tracking-tight"
            >
              {{ option.title }}
            </h2>
            <p
              class="text-black/60 group-hover:text-black/80 leading-relaxed mb-8 text-lg max-w-md"
            >
              {{ option.description }}
            </p>
            <div
              class="inline-flex items-center px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-black/80 transition-colors"
            >
              {{ option.action }}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-24 bg-[#fafafa]">
      <div class="container mx-auto px-6 md:px-4">
        <div class="max-w-[800px] mx-auto">
          <h2 class="text-4xl font-medium text-black/90 mb-12 text-center">
            {{ $t('landingPages.contact.faq.heading') }}
          </h2>
          <div class="space-y-4">
            <div
              v-for="(item, index) in faqItems"
              :key="index"
              class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
            >
              <button
                type="button"
                @click="toggleFaq(index)"
                class="flex items-center justify-between w-full text-left font-medium text-xl text-black/90 focus:outline-none"
              >
                <h3 class="font-medium text-xl text-black/90">{{ item.question }}</h3>
                <span
                  class="transform transition-transform duration-300 text-2xl"
                  :class="{ 'rotate-45': expandedFaqIndex === index }"
                >
                  +
                </span>
              </button>
              <div
                class="grid transition-all duration-300 ease-in-out overflow-hidden"
                :class="
                  expandedFaqIndex === index
                    ? 'grid-rows-[1fr] opacity-100 mt-4'
                    : 'grid-rows-[0fr] opacity-0'
                "
              >
                <div class="overflow-hidden">
                  <!-- 两种结构：
                       a) 简单 Q/A：item.answer 单段
                       b) 含内联链接：item.answerBefore + <a>discordText</a> + item.answerAfter -->
                  <p class="text-black/60 leading-relaxed text-lg">
                    <template v-if="(item as any).answer">
                      {{ (item as any).answer }}
                    </template>
                    <template v-else>
                      {{ (item as any).answerBefore }}
                      <a
                        :href="(item as any).discordLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline inline-flex items-center"
                      >
                        {{ (item as any).discordText }}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          class="w-4 h-4 ml-1"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                      {{ (item as any).answerAfter }}
                    </template>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<style scoped>
.contact-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.contact-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}
</style>
